/*
  Vuetify 3 → Vuetify 2 translations for the M* compat wrappers. Each function takes
  Vuetify 3 API values (see props.ts) and returns what the wrapper binds on its Vue 2
  root. This is the only place a v3 → v2 mapping may be defined.

  A conversion returns only the keys it sets, never `{ xSmall: false }` or
  `{ itemText: undefined }`. When Vue 2 binds a `v-bind` object it skips a key whose
  camelCase or kebab-case twin is already present, whatever that twin's value, so a
  false-valued `xSmall` would hide a consumer's `x-small` attribute spread after it.
*/
import camelCase from 'lodash/camelCase';
import type {
  Anchor,
  BtnVariant,
  Density,
  InputVariant,
  OverlayOffset,
  OverlayTarget,
  Size,
  SelectProps,
  ValidateOn,
} from './props';

export function toV2Density(density: Density | undefined): { dense?: true } {
  return density === 'compact' ? { dense: true } : {};
}

export interface V2InputVariantProps {
  filled?: true;
  outlined?: true;
  solo?: true;
  soloInverted?: true;
}

// Vuetify 2 has no `plain` look: its default draws an underline, which is Vuetify 3's `underlined`.
export function toV2InputVariant(variant: InputVariant): V2InputVariantProps {
  switch (variant) {
    case 'filled':
      return { filled: true };
    case 'outlined':
      return { outlined: true };
    case 'solo':
    case 'solo-filled':
      return { solo: true };
    case 'solo-inverted':
      return { soloInverted: true };
    case 'underlined':
    case 'plain':
      return {};
  }
}

export interface V2BtnVariantProps {
  depressed?: true;
  text?: true;
  outlined?: true;
}

// Vuetify 2.3.7 has neither a tinted `tonal` nor a `plain` button; each falls back to the closest look.
export function toV2BtnVariant(variant: BtnVariant): V2BtnVariantProps {
  switch (variant) {
    case 'flat':
    case 'tonal':
      return { depressed: true };
    case 'text':
    case 'plain':
      return { text: true };
    case 'outlined':
      return { outlined: true };
    case 'elevated':
      return {};
  }
}

export interface V2SizeProps {
  xSmall?: true;
  small?: true;
  large?: true;
  xLarge?: true;
  size?: number;
}

export function toV2Size(size: Size): V2SizeProps {
  switch (size) {
    case 'x-small':
      return { xSmall: true };
    case 'small':
      return { small: true };
    case 'large':
      return { large: true };
    case 'x-large':
      return { xLarge: true };
    case 'default':
      return {};
    default:
      return { size };
  }
}

export function toV2ValidateOn(validateOn: ValidateOn | undefined): { validateOnBlur?: true } {
  return validateOn?.split(' ').includes('blur') ? { validateOnBlur: true } : {};
}

// Vuetify 3 renamed the input affix slots; prepend and prepend-inner are unchanged.
const v2InputSlotNames: Readonly<Record<string, string>> = {
  'append-inner': 'append',
  'append': 'append-outer',
};

export function toV2InputSlotName(v3Name: string): string {
  return v2InputSlotNames[v3Name] ?? v3Name;
}

// Vue 3 consumers may write either case of a kebab event, so both spellings are mapped.
const v2InputEventNames: Readonly<Record<string, string>> = {
  'click:append-inner': 'click:append',
  'click:appendInner': 'click:append',
  'click:append': 'click:append-outer',
  'click:prependInner': 'click:prepend-inner',
};

export function toV2InputEventName(v3Name: string): string {
  return v2InputEventNames[v3Name] ?? v3Name;
}

export function toV2InputListeners<T>(listeners: Readonly<Record<string, T>>): Record<string, T> {
  return Object.fromEntries(Object.entries(listeners).map(([name, listener]) => [toV2InputEventName(name), listener]));
}

// The affix icon props follow the affix slot rename.
const v2InputIconPropNames: Readonly<Record<string, string>> = {
  'append-inner-icon': 'append-icon',
  'appendInnerIcon': 'append-icon',
  'append-icon': 'append-outer-icon',
  'appendIcon': 'append-outer-icon',
};

// Vuetify 3 addresses a non-default icon set with a `set:` prefix; Vuetify 2 resolves `mdi-` names without one.
function toV2IconName(icon: unknown): unknown {
  return typeof icon === 'string' && icon.startsWith('mdi:') ? icon.slice('mdi:'.length) : icon;
}

export function toV2InputAttrs(attrs: Readonly<Record<string, unknown>>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(attrs).map(([name, value]) => {
    const v2Name = v2InputIconPropNames[name];
    return v2Name === undefined ? [name, value] : [v2Name, toV2IconName(value)];
  }));
}

export interface V2SelectProps {
  itemText?: string;
  itemValue?: string;
  deletableChips?: true;
}

export function toV2SelectProps(props: SelectProps): V2SelectProps {
  return {
    ...(props.itemTitle !== undefined && { itemText: props.itemTitle }),
    ...(props.itemValue !== undefined && { itemValue: props.itemValue }),
    ...(props.closableChips && { deletableChips: true }),
  };
}

type AnchorWord = 'top' | 'bottom' | 'start' | 'end' | 'left' | 'right' | 'center';
type PhysicalAnchorWord = 'top' | 'bottom' | 'left' | 'right' | 'center';

/** The side of the activator an overlay opens on, and the activator edge it aligns with. */
interface PhysicalAnchor {
  side: PhysicalAnchorWord;
  align: PhysicalAnchorWord;
}

function toPhysicalAnchorWord(word: AnchorWord): PhysicalAnchorWord {
  if (word === 'start') {
    return 'left';
  }
  if (word === 'end') {
    return 'right';
  }
  return word;
}

function defaultAnchorAlign(side: AnchorWord): AnchorWord {
  if (side === 'top' || side === 'bottom') {
    return 'start';
  }
  return side === 'center' ? 'center' : 'top';
}

/** Vuetify 3's `parseAnchor` for a left-to-right layout. */
function parseOverlayAnchor(location: Anchor): PhysicalAnchor {
  // `Anchor` admits only one or two space-separated anchor words.
  const [side, align] = location.split(' ') as [AnchorWord, AnchorWord | undefined];
  return { side: toPhysicalAnchorWord(side), align: toPhysicalAnchorWord(align ?? defaultAnchorAlign(side)) };
}

export interface V2OverlayLocationProps {
  offsetX?: true;
  offsetY?: true;
  left?: true;
  top?: true;
}

/**
 * Vuetify 2 opens an overlay beside its activator with `offset-x` or below it with
 * `offset-y`, toward the left or top with `left` or `top`. On the axis that is not
 * offset, `left` aligns the right edges and `top` the bottom edges. Setting both offsets
 * would place the overlay off the activator's corner. Center alignment has no equivalent
 * and keeps the start edge; a centered side leaves the overlay over the activator, from
 * its top-left corner.
 */
export function toV2OverlayLocation(location: Anchor): V2OverlayLocationProps {
  const { side, align } = parseOverlayAnchor(location);
  switch (side) {
    case 'top':
      return { offsetY: true, top: true, ...(align === 'right' && { left: true }) };
    case 'bottom':
      return { offsetY: true, ...(align === 'right' && { left: true }) };
    case 'left':
      return { offsetX: true, left: true, ...(align === 'bottom' && { top: true }) };
    case 'right':
      return { offsetX: true, ...(align === 'bottom' && { top: true }) };
    case 'center':
      return {};
  }
}

export interface V2NudgeProps {
  nudgeTop?: number;
  nudgeBottom?: number;
  nudgeLeft?: number;
  nudgeRight?: number;
}

// Vuetify 3 turns a number into `[n, 0]` and parses each space-separated part of a string.
function toOffsetPair(offset: OverlayOffset): [number, number] {
  if (typeof offset === 'number') {
    return [offset, 0];
  }
  if (typeof offset === 'string') {
    const [away = 0, along = 0] = offset.split(' ').map(parseFloat);
    return [away, along];
  }
  return offset;
}

function nudgeToward(direction: PhysicalAnchorWord, distance: number): V2NudgeProps {
  if (distance === 0 || !Number.isFinite(distance)) {
    return {};
  }
  switch (direction) {
    case 'top':
      return { nudgeTop: distance };
    case 'bottom':
      return { nudgeBottom: distance };
    case 'left':
      return { nudgeLeft: distance };
    case 'right':
      return { nudgeRight: distance };
    case 'center':
      return {};
  }
}

/** Moves the overlay away from the activator on its side, and past the edge it aligns with. */
export function toV2OverlayOffset(offset: OverlayOffset | undefined, location: Anchor): V2NudgeProps {
  if (offset === undefined) {
    return {};
  }
  const { side, align } = parseOverlayAnchor(location);
  const [away, along] = toOffsetPair(offset);
  return { ...nudgeToward(side, away), ...nudgeToward(align, along) };
}

export interface V2PositionProps {
  positionX?: number;
  positionY?: number;
  absolute?: true;
}

// The only array an OverlayTarget admits is a coordinate pair.
function isCoordinateTarget(target: OverlayTarget | undefined): target is [number, number] {
  return Array.isArray(target);
}

// Only coordinate targets have a Vuetify 2 equivalent; element and selector targets are dropped.
export function toV2OverlayTarget(target: OverlayTarget | undefined): V2PositionProps {
  if (!isCoordinateTarget(target)) {
    return {};
  }
  const [x, y] = target;
  return { positionX: x, positionY: y, absolute: true };
}

interface V2ActivatorSlotScope {
  attrs?: Record<string, unknown>;
  on?: Record<string, (event: Event) => void>;
}

export interface ActivatorSlotScope {
  props: Record<string, unknown>;
}

/**
 * Gives a Vuetify 2 overlay activator scope the Vuetify 3 `{ props }` shape. Vue 2's
 * `v-bind` does not attach listeners, so the handlers are included as plain props, keyed
 * by event name (`click`, `keydown`, `mouseenter`, `mouseleave`, `focus`, depending on the
 * open triggers), and the activator element must bind them itself, e.g.
 * `@click="props.click?.($event)"`.
 */
export function fromV2ActivatorSlotScope(scope: V2ActivatorSlotScope | undefined): ActivatorSlotScope {
  return {
    props: {
      ...scope?.attrs,
      ...scope?.on,
    },
  };
}

export interface V2DataTableHeader {
  text: string;
  value: string;
  align?: 'start' | 'center' | 'end';
  sortable?: boolean;
  filterable?: boolean;
  groupable?: boolean;
  divider?: boolean;
  class?: string | string[];
  width?: string | number;
  filter?: (value: unknown, search: string | null, item: unknown) => boolean;
  sort?: (a: unknown, b: unknown) => number;
}

/** The Vuetify 3 `DataTableHeader` fields that have a Vuetify 2 equivalent. */
export interface V3DataTableHeaderFields {
  key?: string;
  title?: string;
  align?: 'start' | 'end' | 'center';
  sortable?: boolean;
  width?: number | string;
  headerProps?: Record<string, unknown>;
}

/** Vuetify 3 `SortItem`. */
export interface V3SortItem {
  key: string;
  order: 'asc' | 'desc';
}

export function toV2DataTableHeaders(headers: readonly V3DataTableHeaderFields[]): V2DataTableHeader[] {
  return headers.map((h): V2DataTableHeader => ({
    text: h.title ?? '',
    value: typeof h.key === 'string' ? h.key : '',
    align: h.align,
    sortable: h.sortable ?? true,
    width: h.width,
    ...(h.headerProps !== undefined && { class: h.headerProps['class'] as string }),
  }));
}

// Vuetify 3 VDataTable props that Vuetify 2 expects wrapped in `footer-props`.
const v2DataTableFooterPropNames = ['itemsPerPageText', 'showCurrentPage', 'pageText', 'showFirstLastPage'];

/**
 * Translates the attrs bound on a Vuetify 3 data table into Vuetify 2 props. Attr names
 * are camelized first: Vue 2 matches kebab-case attrs to props itself, but not the keys
 * of an object this function builds.
 */
export function toV2DataTableProps(attrs: Readonly<Record<string, unknown>>, sortBy: readonly V3SortItem[] | undefined): Record<string, unknown> {
  const mapped: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(attrs)) {
    mapped[camelCase(key)] = value;
  }

  if (mapped['density'] === 'compact') {
    mapped['dense'] = true;
    delete mapped['density'];
  }

  if ('itemValue' in mapped && !('itemKey' in mapped)) {
    mapped['itemKey'] = mapped['itemValue'];
    delete mapped['itemValue'];
  }

  // Vuetify 3 has no no-results-text; Vuetify 2 shows it for a filtered-to-empty table.
  // Consumers put contextual text in no-data-text to cover both cases.
  if ('noDataText' in mapped && !('noResultsText' in mapped)) {
    mapped['noResultsText'] = mapped['noDataText'];
  }

  const footerProps: Record<string, unknown> = {};
  for (const name of v2DataTableFooterPropNames) {
    if (name in mapped) {
      footerProps[name] = mapped[name];
      delete mapped[name];
    }
  }
  if (Object.keys(footerProps).length > 0) {
    mapped['footerProps'] = footerProps;
  }

  const [firstSort] = sortBy ?? [];
  if (firstSort !== undefined) {
    mapped['sortBy'] = firstSort.key;
    mapped['sortDesc'] = firstSort.order === 'desc';
  }
  return mapped;
}

/** Translates a Vuetify 2 `update:sort-by` payload back to Vuetify 3 sort items, keeping the order of known keys. */
export function fromV2DataTableSortBy(value: string | string[] | undefined, current: readonly V3SortItem[] | undefined): V3SortItem[] {
  const keys = [value ?? []].flat().filter(key => key !== '');
  return keys.map((key): V3SortItem => current?.find(s => s.key === key) ?? { key, order: 'asc' });
}

const v2DataTableSlotNames: Readonly<Record<string, string>> = {
  'expanded-row': 'expanded-item',
  'thead': 'header',
  'tfoot': 'body.append',
  'bottom': 'footer',
  'data-table-group': 'group',
  'group-header': 'group.header',
  'group-summary': 'group.summary',
  'loader': 'progress',
};

export function toV2DataTableSlotName(v3Name: string): string {
  return v2DataTableSlotNames[v3Name] ?? v3Name;
}

/** Adds the Vuetify 3 slot scope names as aliases of their Vuetify 2 equivalents. */
export function toV2DataTableSlotData(v3Name: string, data: Readonly<Record<string, unknown>>): Record<string, unknown> {
  if (v3Name === 'item' || v3Name.startsWith('item.')) {
    return { ...data, columns: data['headers'], toggleExpand: data['expand'], toggleSelect: data['select'] };
  }
  if (v3Name === 'expanded-row' || v3Name === 'body.append' || v3Name === 'body.prepend') {
    return { ...data, columns: data['headers'] };
  }
  return { ...data };
}
